import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import {
  Whatshot as WarmUpIcon,
  Send as DMIcon,
  Phone as CallIcon,
  EventBusy as OverdueIcon,
  ArrowForward as ArrowIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { getInstagramDmUrl, formatInstagramHandle } from '../../utils/helpers';

const TaskSection = ({ icon, iconColor, title, count, leads, actionLabel, onAction }) => {
  const navigate = useNavigate();

  if (count === 0) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Avatar sx={{ width: 24, height: 24, bgcolor: `${iconColor}.light`, color: `${iconColor}.dark` }}>
          {React.cloneElement(icon, { sx: { fontSize: 14 } })}
        </Avatar>
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Chip label={count} size="small" color={iconColor} sx={{ fontWeight: 600, height: 22, fontSize: '0.75rem' }} />
      </Box>

      <List dense disablePadding>
        {leads?.slice(0, 3).map((lead) => (
          <ListItem
            key={lead._id}
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              '&:hover': { bgcolor: 'grey.50' },
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/leads/${lead._id}`)}
            secondaryAction={
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(getInstagramDmUrl(lead.instagramHandle), '_blank');
                }}
              >
                <InstagramIcon sx={{ fontSize: 16 }} />
              </IconButton>
            }
          >
            <ListItemText
              primary={formatInstagramHandle(lead.instagramHandle)}
              secondary={lead.businessName}
              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItem>
        ))}
      </List>

      {count > 3 && (
        <Button
          size="small"
          endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
          onClick={onAction}
          sx={{ ml: 0.5, mt: 0.25, fontSize: '0.7rem', py: 0.25 }}
        >
          View all {count}
        </Button>
      )}
    </Box>
  );
};

const TodayTasksCard = ({ tasks }) => {
  const navigate = useNavigate();

  const totalTasks = (tasks?.warmUp?.count || 0) +
    (tasks?.readyToDm?.count || 0) +
    (tasks?.followUps?.count || 0) +
    (tasks?.overdue?.count || 0) +
    (tasks?.scheduledCalls?.count || 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Today's Tasks
          </Typography>
          <Chip
            label={`${totalTasks} total`}
            size="small"
            variant="outlined"
            sx={{ height: 24, fontSize: '0.75rem' }}
          />
        </Box>

        {totalTasks === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              No tasks for today! Great job staying on top of things.
            </Typography>
          </Box>
        ) : (
          <>
            <TaskSection
              icon={<OverdueIcon fontSize="small" />}
              iconColor="error"
              title="Overdue"
              count={tasks?.overdue?.count}
              leads={tasks?.overdue?.leads}
              onAction={() => navigate('/leads?hasOverdueFollowup=true')}
            />

            <TaskSection
              icon={<WarmUpIcon fontSize="small" />}
              iconColor="warning"
              title="Warm Up"
              count={tasks?.warmUp?.count}
              leads={tasks?.warmUp?.leads}
              onAction={() => navigate('/leads?stage=warming')}
            />

            <TaskSection
              icon={<DMIcon fontSize="small" />}
              iconColor="info"
              title="Ready to DM"
              count={tasks?.readyToDm?.count}
              leads={tasks?.readyToDm?.leads}
              onAction={() => navigate('/leads?stage=dm_sent')}
            />

            <TaskSection
              icon={<CallIcon fontSize="small" />}
              iconColor="secondary"
              title="Calls Today"
              count={tasks?.scheduledCalls?.count}
              leads={tasks?.scheduledCalls?.leads}
              onAction={() => navigate('/leads?stage=call_scheduled')}
            />

            <TaskSection
              icon={<ArrowIcon fontSize="small" />}
              iconColor="primary"
              title="Follow-ups Due"
              count={tasks?.followUps?.count}
              leads={tasks?.followUps?.leads}
              onAction={() => navigate('/leads?hasFollowupToday=true')}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TodayTasksCard;
